/**
 * 🎨 SPACE LOYALTY CARDS - Module Cartes de Fidélité
 * Création et gestion des templates de cartes personnalisables
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CreditCard, Plus, Edit, Trash2, Eye, EyeOff, Gift, 
  Loader2, Save, X, Upload, Palette, Hash, Award,
  Wine, Leaf, Cigarette, Beer, Coffee, Pizza, Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { api } from '../../services/api';
import { toast } from 'sonner';

// Icônes disponibles pour les cartes
const AVAILABLE_ICONS = [
  { id: 'wine', icon: Wine, label: 'Vin/Alcool' },
  { id: 'leaf', icon: Leaf, label: 'CBD/Nature' },
  { id: 'cigarette', icon: Cigarette, label: 'Puffs/Vape' },
  { id: 'beer', icon: Beer, label: 'Bière' },
  { id: 'coffee', icon: Coffee, label: 'Café' },
  { id: 'pizza', icon: Pizza, label: 'Food' },
  { id: 'gift', icon: Gift, label: 'Cadeau' },
  { id: 'sparkles', icon: Sparkles, label: 'Spécial' },
];

// Couleurs prédéfinies
const PRESET_COLORS = [
  { color: '#722F37', accent: '#D4AF37', label: 'Bordeaux/Or' },
  { color: '#2D5A45', accent: '#66BB6A', label: 'Vert Nature' },
  { color: '#6366F1', accent: '#06B6D4', label: 'Bleu Électrique' },
  { color: '#B45309', accent: '#FCD34D', label: 'Ambre/Bière' },
  { color: '#7C3AED', accent: '#EC4899', label: 'Violet/Rose' },
  { color: '#0F766E', accent: '#2DD4BF', label: 'Teal' },
  { color: '#DC2626', accent: '#FB923C', label: 'Rouge/Orange' },
  { color: '#1F2937', accent: '#9CA3AF', label: 'Noir/Gris' },
];

// Prévisualisation de carte
const CardPreview = ({ template }) => {
  const IconComponent = AVAILABLE_ICONS.find(i => i.id === template.icon)?.icon || Gift;
  
  return (
    <div 
      className="rounded-2xl overflow-hidden shadow-lg"
      style={{ maxWidth: '300px' }}
    >
      {/* Header */}
      <div
        className="p-4 relative h-24"
        style={{
          background: `linear-gradient(135deg, ${template.color}99 0%, ${template.accentColor || template.color}80 100%)`,
          backgroundImage: template.bgImage 
            ? `linear-gradient(135deg, ${template.color}90 0%, ${template.accentColor || template.color}80 100%), url(${template.bgImage})`
            : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="flex items-center gap-3">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
          >
            <IconComponent className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">{template.name || 'Nouvelle Carte'}</h3>
            <p className="text-white/70 text-sm">{template.description || 'Description...'}</p>
          </div>
        </div>
      </div>
      
      {/* Body */}
      <div className="bg-white p-4">
        {/* Cases de tampons (preview) */}
        <div className="grid grid-cols-5 gap-1.5 mb-3">
          {Array(Math.min(template.boxCount || 10, 10)).fill(0).map((_, i) => (
            <div
              key={i}
              className="aspect-square rounded-md border-2 flex items-center justify-center"
              style={{
                borderColor: i < 3 ? template.color : '#CBD5E1',
                backgroundColor: i < 3 ? template.color : 'white'
              }}
            >
              {i < 3 && <span className="text-white text-xs">✓</span>}
            </div>
          ))}
        </div>
        
        {/* Récompense */}
        <div 
          className="text-center py-2 rounded-lg text-sm font-semibold"
          style={{ backgroundColor: `${template.color}15`, color: template.color }}
        >
          🎁 {template.reward || 'Récompense gratuite'}
        </div>
      </div>
    </div>
  );
};

// Formulaire de création/édition
const CardTemplateForm = ({ template, onSave, onClose, isLoading }) => {
  const [form, setForm] = useState({
    name: template?.name || '',
    description: template?.description || '',
    reward: template?.reward || '',
    boxCount: template?.boxCount || 10,
    color: template?.color || '#722F37',
    accentColor: template?.accentColor || '#D4AF37',
    icon: template?.icon || 'gift',
    bgImage: template?.bgImage || '',
    isActive: template?.isActive !== false
  });

  const updateForm = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }
    if (!form.reward.trim()) {
      toast.error('La récompense est obligatoire');
      return;
    }
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        {/* Colonne gauche - Formulaire */}
        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Nom de la carte *</label>
            <Input
              value={form.name}
              onChange={(e) => updateForm('name', e.target.value)}
              placeholder="Ex: Pack Bières, Labobos..."
              className="bg-[#1a1a25] border-cyan-500/30 text-white"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-1 block">Description</label>
            <Input
              value={form.description}
              onChange={(e) => updateForm('description', e.target.value)}
              placeholder="Ex: 1 tampon par pack acheté"
              className="bg-[#1a1a25] border-cyan-500/30 text-white"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-1 block">Récompense *</label>
            <Input
              value={form.reward}
              onChange={(e) => updateForm('reward', e.target.value)}
              placeholder="Ex: Pack gratuit"
              className="bg-[#1a1a25] border-cyan-500/30 text-white"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-1 block">Nombre de tampons</label>
            <div className="flex gap-2">
              {[5, 10, 15, 20, 40].map(num => (
                <button
                  key={num}
                  type="button"
                  onClick={() => updateForm('boxCount', num)}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all ${
                    form.boxCount === num 
                      ? 'bg-cyan-500 text-white' 
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-1 block">Icône</label>
            <div className="flex gap-2 flex-wrap">
              {AVAILABLE_ICONS.map(({ id, icon: Icon, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => updateForm('icon', id)}
                  title={label}
                  className={`p-2 rounded-lg transition-all ${
                    form.icon === id 
                      ? 'bg-cyan-500 text-white' 
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-1 block">Couleurs</label>
            <div className="flex gap-2 flex-wrap">
              {PRESET_COLORS.map(({ color, accent, label }) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => {
                    updateForm('color', color);
                    updateForm('accentColor', accent);
                  }}
                  title={label}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    form.color === color ? 'border-white scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-1 block">Image de fond (URL)</label>
            <Input
              value={form.bgImage}
              onChange={(e) => updateForm('bgImage', e.target.value)}
              placeholder="https://... ou laisser vide"
              className="bg-[#1a1a25] border-cyan-500/30 text-white"
            />
          </div>
        </div>

        {/* Colonne droite - Prévisualisation */}
        <div>
          <label className="text-sm text-gray-400 mb-3 block">Prévisualisation</label>
          <CardPreview template={form} />
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-gray-700">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1">
          Annuler
        </Button>
        <Button 
          type="submit" 
          disabled={isLoading}
          className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600"
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          {template ? 'Modifier' : 'Créer'}
        </Button>
      </div>
    </form>
  );
};

// Carte template dans la liste
const TemplateCard = ({ template, onEdit, onToggle, onDelete }) => {
  const IconComponent = AVAILABLE_ICONS.find(i => i.id === template.icon)?.icon || Gift;
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl overflow-hidden border ${
        template.isActive 
          ? 'border-cyan-500/30 bg-[#12121a]/80' 
          : 'border-gray-700/30 bg-gray-900/50 opacity-60'
      }`}
    >
      {/* Header coloré */}
      <div
        className="p-4 relative"
        style={{
          background: `linear-gradient(135deg, ${template.color}80 0%, ${template.accentColor}60 100%)`
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
            >
              <IconComponent className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-white font-bold">{template.name}</h3>
              <p className="text-white/70 text-sm">{template.boxCount} tampons</p>
            </div>
          </div>
          
          {template.isDefault && (
            <span className="px-2 py-1 rounded-full bg-white/20 text-white text-xs">
              Par défaut
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-4">
        <p className="text-gray-400 text-sm mb-2">{template.description}</p>
        <p className="text-green-400 text-sm flex items-center gap-1">
          <Gift className="w-4 h-4" />
          {template.reward}
        </p>

        {/* Actions */}
        <div className="flex gap-2 mt-4">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onEdit(template)}
            className="flex-1 border-cyan-500/30 text-cyan-400"
          >
            <Edit className="w-4 h-4 mr-1" /> Modifier
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onToggle(template.id)}
            className={`border-${template.isActive ? 'yellow' : 'green'}-500/30 text-${template.isActive ? 'yellow' : 'green'}-400`}
          >
            {template.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
          {!template.isDefault && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDelete(template.id)}
              className="border-red-500/30 text-red-400"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Module principal
const SpaceLoyaltyCards = () => {
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Charger les templates
  const loadTemplates = async () => {
    setIsLoading(true);
    try {
      const data = await api.getCardTemplates(true); // Inclure les inactifs
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Erreur chargement templates:', error);
      toast.error('Erreur de chargement des templates');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  // Créer un template
  const handleCreate = async (formData) => {
    setIsSaving(true);
    try {
      await api.createCardTemplate(formData);
      toast.success('🎉 Nouvelle carte créée !');
      setShowCreateModal(false);
      loadTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de création');
    } finally {
      setIsSaving(false);
    }
  };

  // Modifier un template
  const handleUpdate = async (formData) => {
    setIsSaving(true);
    try {
      await api.updateCardTemplate(editingTemplate.id, formData);
      toast.success('✅ Carte modifiée !');
      setEditingTemplate(null);
      loadTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de modification');
    } finally {
      setIsSaving(false);
    }
  };

  // Toggle actif/inactif
  const handleToggle = async (templateId) => {
    try {
      const result = await api.toggleCardTemplate(templateId);
      toast.success(result.isActive ? '✅ Carte activée' : '⏸️ Carte désactivée');
      loadTemplates();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Supprimer
  const handleDelete = async (templateId) => {
    const code = window.prompt('Code admin pour supprimer :');
    if (!code) return;
    
    try {
      await api.deleteCardTemplate(templateId, code);
      toast.success('🗑️ Carte supprimée');
      loadTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code invalide');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-500">
            🎨 CARTES DE FIDÉLITÉ
          </h1>
          <p className="text-gray-500 text-sm">Créez et gérez vos cartes personnalisées</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-pink-500 to-purple-600"
        >
          <Plus className="w-4 h-4 mr-2" /> Nouvelle Carte
        </Button>
      </div>

      {/* Liste des templates */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={setEditingTemplate}
              onToggle={handleToggle}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Modal création */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-[#0a0a12] border-cyan-500/30 max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-400" />
              Créer une nouvelle carte
            </DialogTitle>
          </DialogHeader>
          <CardTemplateForm
            onSave={handleCreate}
            onClose={() => setShowCreateModal(false)}
            isLoading={isSaving}
          />
        </DialogContent>
      </Dialog>

      {/* Modal édition */}
      <Dialog open={!!editingTemplate} onOpenChange={() => setEditingTemplate(null)}>
        <DialogContent className="bg-[#0a0a12] border-cyan-500/30 max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <Edit className="w-5 h-5 text-cyan-400" />
              Modifier la carte
            </DialogTitle>
          </DialogHeader>
          {editingTemplate && (
            <CardTemplateForm
              template={editingTemplate}
              onSave={handleUpdate}
              onClose={() => setEditingTemplate(null)}
              isLoading={isSaving}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SpaceLoyaltyCards;
